import {
  IPayment,
  IPayments,
  ISystemBank,
} from "@/core/interfaces/state/payments.interface";
import { PaymentsResponseModel } from "@/modules/Payments/models/payments.response";
import { PAYMENT_METHODS_TYPES } from "@/core/enums/PAYMENT_METHODS_TYPES";
import { PAYMENT_STATUS } from "@/core/enums/PAYMENT_STATUS";
import { RECEIPT_STATUS } from "@/core/enums/RECEIPT_STATUS";
import {
  IBankTransferImage,
  IBankTransferPaymentDetail,
  PaymentDetailResponseModel,
} from "@/modules/Payments/models/paymentDetail.response";

export class PaymentsMapper {
  private payments: IPayments = {
    pageNumber: 0,
    pageSize: 0,
    hasNext: false,
    totalPages: 0,
    totalRecords: 0,
    payments: [],
  };

  private payment: IPayment = {
    id: "",
    transactionId: "string",
    transactionType: PAYMENT_METHODS_TYPES.ONE_BILL,
    core_charges: null,
    core_amount: null,
    financialProfile: "",
    dateTime: new Date(),
    paymentStatus: PAYMENT_STATUS.INITIATED,
    oneBillDetail: null,
    creditCardlDetail: null,
    bankTransferDetail: null,
    receiptNumber: null,
    description: "",
    receiptStatus: RECEIPT_STATUS.PAYMENT_PENDING,
    ibftDetail: null,
    organizationUserEmail: "",
    organizationUserName: "",
  };

  public mapPaymentsResponse(data: PaymentsResponseModel): IPayments {
    this.payments.pageSize = data.pagination_meta.page_size;
    this.payments.hasNext = data.pagination_meta.has_next;
    this.payments.totalPages = data.pagination_meta.total_pages;
    this.payments.totalRecords = data.pagination_meta.total_records;
    this.payments.pageNumber = data.pagination_meta.page_no;
    this.payments.payments = data.payments.map((item) => ({
      id: item.payment_id,
      transactionId: item.payment_id,
      core_amount: item.core_amount,
      core_charges: item.core_charges,
      financialProfile: item.financial_profile,
      dateTime: new Date(item.created_at),
      transactionType: item.method as PAYMENT_METHODS_TYPES,
      paymentStatus: item.payment_status as PAYMENT_STATUS,
      receiptStatus: item.receipt_status as RECEIPT_STATUS,
      receiptNumber: item.receipt_no ?? null,
      description: item.description,
      creditCardlDetail: null,
      oneBillDetail: null,
      organizationUserEmail:
        item.organization_user_details.organization_user_email,
      organizationUserName:
        item.organization_user_details.organization_user_name,
      ibftDetail: null,
      bankTransferDetail: null,
    }));

    return this.payments;
  }

  public mapPaymentResponse(data: PaymentDetailResponseModel): IPayment {
    this.payment.id = data.payment.payment_id;
    this.payment.transactionId = data.payment.payment_id;
    this.payment.description = data.payment.description;
    this.payment.core_amount = data.payment?.core_amount;
    this.payment.core_charges = data.payment?.core_charges;
    this.payment.financialProfile = data.payment.financial_profile;
    this.payment.dateTime = new Date(data.payment.created_at);
    this.payment.transactionType = data.payment.method as PAYMENT_METHODS_TYPES;
    this.payment.paymentStatus = data.payment.payment_status as PAYMENT_STATUS;
    this.payment.receiptStatus = data.payment.receipt_status as RECEIPT_STATUS;
    this.payment.receiptNumber = data.payment.receipt_no ?? null;
    this.payment.organizationUserEmail =
      data.payment.organization_user_details.organization_user_email;
    this.payment.organizationUserName =
      data.payment.organization_user_details.organization_user_name;

    if (data.payment.credit_card_payment_detail) {
      this.payment.creditCardlDetail = {
        paymentId: data.payment.credit_card_payment_detail.payment_id,
        paymentCode: data.payment.credit_card_payment_detail.payment_code,
        paymentUrl: data.payment.credit_card_payment_detail.payment_url,
        expiryDate: data.payment.credit_card_payment_detail.expiry_date,
      };
    }
    if (data.payment.one_bill_payment_detail) {
      this.payment.oneBillDetail = {
        billCode: data.payment.one_bill_payment_detail.bill_code,
        billId: data.payment.one_bill_payment_detail.bill_id,
        expiryDate: data.payment.one_bill_payment_detail.expiry_date,
      };
    }
    if (data.payment.ibft_payment_detail) {
      this.payment.ibftDetail = {
        paymentCode: data.payment.ibft_payment_detail.payment_code,
      };
    }
    if (data.payment.bank_transfer_payment_detail) {
      this.payment.bankTransferDetail = {
        bank_transfer_status:
          data.payment.bank_transfer_payment_detail.bank_transfer_status,
        transaction_id:
          data.payment.bank_transfer_payment_detail.transaction_id,
        remarks: data.payment.bank_transfer_payment_detail.remarks,
        paid_on: data.payment.bank_transfer_payment_detail.paid_on,
        images: data.payment.bank_transfer_payment_detail
          .images as IBankTransferImage[],
        bank: data.payment.bank_transfer_payment_detail.bank as ISystemBank,
      } as IBankTransferPaymentDetail;
    }
    return this.payment;
  }

  public mapCreatePaymentResponse(data: PaymentDetailResponseModel): {
    id: string;
  } {
    const payload = {
      id: "",
    };

    payload.id = data.payment.payment_id;
    return payload;
  }
}
